import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subject, timer, of } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { SupportService } from '../support.service';
import { AssistantService } from '../assistant.service';
import { UserServiceInterface } from '@hutsix/ngxh6';
import { environment } from '../../../../environments/environment';
import { isDemoExec } from '../../_shared/demo-exec';

export type WidgetView = 'closed' | 'list' | 'new' | 'detail' | 'chat';

@Component({
    selector: 'app-support-widget',
    standalone: false,
    templateUrl: './support-widget.component.html',
    styleUrls: ['./support-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportWidgetComponent implements OnInit, OnDestroy {
    view: WidgetView = 'closed';
    selectedTicketKey: string | null = null;
    // A just-created ticket, shown optimistically in the list until Jira's
    // search index returns it. Cleared when the panel closes.
    pendingTicket: any = null;
    orgName = '';
    enabled = false;

    // Notifications
    unreadCount = 0;
    private readonly destroy$ = new Subject<void>();
    private readonly stopPoll$ = new Subject<void>();

    // Admin connection state
    isAdmin = false;
    connected: boolean | null = null; // null = loading

    constructor(
        public supportService: SupportService,
        public assistantService: AssistantService,
        @Inject('UserService') public userService: UserServiceInterface,
        private cdr: ChangeDetectorRef,
        private el: ElementRef,
    ) {}

    ngOnInit(): void {
        this.enabled = this.supportService.isEnabled;
        this.orgName = (environment as any).support?.orgName ?? '';
        this.isAdmin = isDemoExec(this.userService.user);

        // Apply theme color from environment config
        const color = (environment as any).support?.primaryColor;
        if (color) {
            const host = this.el.nativeElement as HTMLElement;
            host.style.setProperty('--sw-primary', color);
            host.style.setProperty('--sw-primary-hover', this.adjustBrightness(color, 20));
        }

        // Poll for ticket updates to drive the unread badge.
        if (this.enabled) {
            const intervalMs = (environment as any).support?.pollIntervalMs ?? 180000;
            if (intervalMs > 0) {
                timer(0, intervalMs)
                    .pipe(
                        takeUntil(this.stopPoll$),
                        takeUntil(this.destroy$),
                        switchMap(() =>
                            this.supportService.listTickets(20, undefined, false).pipe(
                                catchError((err: any) => {
                                    if (err?.status === 503) {
                                        this.stopPoll$.next();
                                    }
                                    return of(null);
                                }),
                            ),
                        ),
                    )
                    .subscribe((data: any) => this.applyUnread(data));
            } else {
                // pollIntervalMs: 0 disables background polling — one initial fetch only (matches vanilla).
                this.refreshUnread();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.stopPoll$.complete();
    }

    private applyUnread(data: any): void {
        if (data && typeof data.unreadCount === 'number') {
            this.unreadCount = data.unreadCount;
            this.cdr.markForCheck();
        }
    }

    /** One-off refresh (after opening/confirming a ticket clears unread state). */
    refreshUnread(): void {
        this.supportService
            .listTickets(20, undefined, false)
            .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
            .subscribe((data: any) => this.applyUnread(data));
    }

    private adjustBrightness(hex: string, amount: number): string {
        const num = parseInt(hex.replace('#', ''), 16);
        const r = Math.min(255, ((num >> 16) & 0xff) + amount);
        const g = Math.min(255, ((num >> 8) & 0xff) + amount);
        const b = Math.min(255, (num & 0xff) + amount);
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    }

    toggle(): void {
        if (this.view === 'closed') {
            this.view = 'list';
            this.selectedTicketKey = null;
            // Check connection status when admin opens the panel
            if (this.isAdmin && this.connected === null) {
                this.checkConnection();
            }
            this.refreshUnread();
        } else {
            this.view = 'closed';
        }
    }

    close(): void {
        this.view = 'closed';
        this.selectedTicketKey = null;
        // Fresh state next open — by then the search index will return the ticket.
        this.pendingTicket = null;
    }

    showNewTicket(): void {
        this.view = 'new';
    }

    showList(): void {
        this.view = 'list';
        this.selectedTicketKey = null;
        this.refreshUnread();
    }

    showChat(): void {
        this.view = 'chat';
        this.selectedTicketKey = null;
    }

    showDetail(issueKey: string): void {
        this.selectedTicketKey = issueKey;
        this.view = 'detail';
    }

    onTicketCreated(created: any): void {
        this.pendingTicket = created ?? null;
        this.view = 'list';
        this.refreshUnread();
        this.cdr.markForCheck();
    }

    onTicketConfirmed(): void {
        this.view = 'list';
        this.selectedTicketKey = null;
        this.refreshUnread();
        this.cdr.markForCheck();
    }

    // Admin: check if Jira is connected
    checkConnection(): void {
        this.supportService.checkConnection().subscribe({
            next: (res: any) => {
                this.connected = res.connected ?? false;
                this.cdr.markForCheck();
            },
            error: () => {
                // The demo has no service desk to probe: only an explicit
                // `connected: false` swaps the ticket list for the setup panel.
                this.connected = (environment as any).demo ? true : false;
                this.cdr.markForCheck();
            },
        });
    }

}
